/**
 * PURPOSE:
 * This file defines foundational building blocks for dropdown menus, based off of the Radix UI primitives.
 * Re-use these components to create dropdown menus in the app, if the pre-packaged components don't fit your needs.
 *
 * @todo We want to re-use existing components instead of the heavy re-styling?
 *       Look as `asChild` pattern once we've got a decent grasp on performance impacts.
 */

import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { ChevronRight } from '@timelyapp/icons/line-icons';
import classNames from 'classnames';
import React from 'react';
import { Box } from '../../box/Box';
import { Text } from '../../text/Text';
import sharedStyles from '../radix-floating-elements.module.scss';
import styles from './DropdownMenuPrimitive.module.scss';

export type { DropdownMenuItem as DropdownMenuItemPrimitive } from '@radix-ui/react-dropdown-menu';

import { autoIconMap } from '../../message/Message';
import type {
  DropdownMenuVariant,
  DropdownMessageOption,
  ShortcutCombo as ShortcutComboType,
} from './DropdownMenu.types';

const DropdownMenuRoot = (
  props: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Root>
) => <DropdownMenuPrimitive.Root modal {...props} />;

const DropdownMenuTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger>
>((props, forwardedRef) => <DropdownMenuPrimitive.Trigger {...props} ref={forwardedRef} />);
DropdownMenuTrigger.displayName = 'DropdownMenuTrigger';

const DropdownMenuContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>((props, forwardedRef) => (
  <DropdownMenuPrimitive.Content
    sideOffset={5}
    collisionPadding={4 * 4}
    {...props}
    ref={forwardedRef}
    className={classNames(sharedStyles.dropdownContentBase, props.className)}
  />
));
DropdownMenuContent.displayName = 'DropdownMenuContent';

const DropdownMenuLabel = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>((props, forwardedRef) => (
  <DropdownMenuPrimitive.Label
    {...props}
    ref={forwardedRef}
    className={classNames(sharedStyles.dropdownMenuLabel, props.className)}
  />
));
DropdownMenuLabel.displayName = 'DropdownMenuLabel';

type DropdownMenuItemProps = React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
  orientation?: 'vertical' | 'horizontal';
  variant?: DropdownMenuVariant;
};

const DropdownMenuItem = React.forwardRef<HTMLDivElement, DropdownMenuItemProps>(
  ({ children, orientation = 'horizontal', variant, ...props }, forwardedRef) => {
    return (
      <DropdownMenuPrimitive.Item
        {...props}
        ref={forwardedRef}
        className={classNames(
          sharedStyles.dropdownMenuItemBase,
          sharedStyles[`orientation-${orientation}`],
          variant && styles[`variant-${variant}`]
        )}
      >
        {children}
      </DropdownMenuPrimitive.Item>
    );
  }
);
DropdownMenuItem.displayName = 'DropdownMenuItem';

const DropdownMenuCustomItem = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<'div'>
>((props, forwardedRef) => (
  <div
    {...props}
    ref={forwardedRef}
    className={classNames(sharedStyles.dropdownMenuItemBase, sharedStyles.dropdownMenuItemCustom)}
  />
));
DropdownMenuCustomItem.displayName = 'DropdownMenuCustomItem';

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

type DropdownMenuCheckboxItemProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.CheckboxItem
> & {
  variant?: DropdownMenuVariant;
};

const DropdownMenuCheckboxItem = React.forwardRef<HTMLDivElement, DropdownMenuCheckboxItemProps>(
  ({ variant, ...props }, forwardedRef) => {
    return (
      <DropdownMenuPrimitive.CheckboxItem
        {...props}
        ref={forwardedRef}
        className={classNames(
          sharedStyles.dropdownMenuItemBase,
          variant && styles[`variant-${variant}`]
        )}
      />
    );
  }
);
DropdownMenuCheckboxItem.displayName = 'DropdownMenuCheckboxItem';

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

type DropdownMenuRadioItemProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.RadioItem
> & {
  variant?: DropdownMenuVariant;
};

const DropdownMenuRadioItem = React.forwardRef<HTMLDivElement, DropdownMenuRadioItemProps>(
  ({ variant, ...props }, forwardedRef) => {
    return (
      <DropdownMenuPrimitive.RadioItem
        {...props}
        ref={forwardedRef}
        className={classNames(
          sharedStyles.dropdownMenuItemBase,
          variant && styles[`variant-${variant}`]
        )}
      />
    );
  }
);
DropdownMenuRadioItem.displayName = 'DropdownMenuRadioItem';

const DropdownMenuSeparator = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>((props, forwardedRef) => (
  <DropdownMenuPrimitive.Separator
    data-separator="true"
    {...props}
    ref={forwardedRef}
    className={sharedStyles.dropdownMenuSeparator}
  />
));
DropdownMenuSeparator.displayName = 'DropdownMenuSeparator';

const DropdownMenuArrow = React.forwardRef<
  SVGSVGElement,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Arrow>
>((props, forwardedRef) => (
  <DropdownMenuPrimitive.Arrow {...props} ref={forwardedRef} className={styles.dropdownMenuArrow} />
));
DropdownMenuArrow.displayName = 'DropdownMenuArrow';

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

type DropdownMenuSubTriggerProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.SubTrigger
> & {
  variant?: DropdownMenuVariant;
};

const DropdownMenuSubTrigger = React.forwardRef<HTMLDivElement, DropdownMenuSubTriggerProps>(
  ({ variant, ...props }, forwardedRef) => {
    return (
      <DropdownMenuPrimitive.SubTrigger
        {...props}
        ref={forwardedRef}
        className={classNames(
          sharedStyles.dropdownMenuItemBase,
          variant && styles[`variant-${variant}`]
        )}
      />
    );
  }
);
DropdownMenuSubTrigger.displayName = 'DropdownMenuSubTrigger';

const DropdownMenuSubContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>((props, forwardedRef) => (
  <DropdownMenuPrimitive.SubContent
    sideOffset={2}
    alignOffset={-5}
    collisionPadding={4 * 4}
    {...props}
    ref={forwardedRef}
    className={classNames(sharedStyles.dropdownContentBase, sharedStyles.dropdownSubContent)}
  />
));
DropdownMenuSubContent.displayName = 'DropdownMenuSubContent';

const DropdownMenuItemIndicator = React.forwardRef<
  HTMLSpanElement,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.ItemIndicator>
>((props, forwardedRef) => (
  <DropdownMenuPrimitive.ItemIndicator
    {...props}
    ref={forwardedRef}
    className={sharedStyles.dropdownMenuItemIndicator}
  />
));
DropdownMenuItemIndicator.displayName = 'DropdownMenuItemIndicator';

/**
 *
 * Below are home-grown components, NOT pact of the Radix UI primitives.
 * (though demonstrated in their examples)
 */

type RightSlotProps = {
  children?: React.ReactNode;
};

const RightSlot = ({ children, ...props }: RightSlotProps) => (
  <div {...props} className={styles.rightSlot}>
    {children || <ChevronRight />}
  </div>
);

const ShortcutCombo = ({ combo }: { combo?: ShortcutComboType }) => {
  if (!combo) return null;
  return (
    <RightSlot>
      {combo[0] === 'meta' && '⌘'}
      {combo[0] === 'alt' && '⌥'}
      {combo[0] === 'ctrl' && '⌃'}+{combo[1]}
      {combo[2] && `+${combo[2]}`}
    </RightSlot>
  );
};

type MessageProps = {
  item: DropdownMessageOption;
};
const Message = ({ item, ...rest }: MessageProps) => {
  const variant = item.variant || 'info';

  // Determine which icon to use
  let finalIcon: React.ReactNode = null;
  if (item.icon === 'auto') {
    finalIcon = autoIconMap[variant as keyof typeof autoIconMap];
  } else if (item.icon !== false && React.isValidElement(item.icon)) {
    finalIcon = item.icon;
  }

  return (
    <Box.Row
      gap="medium"
      {...rest}
      data-readonly
      className={classNames(styles.messageBody, styles[`variant-${variant}`])}
    >
      {!!finalIcon && (
        <Box className={styles.messageIcon} alignSelf="flex-start">
          {finalIcon}
        </Box>
      )}
      <Box.Column>
        {item.label && <Text.Label size="small">{item.label}</Text.Label>}
        {item.description && (
          <Text size="small" color="subtle">
            {item.description}
          </Text>
        )}
        {item.content}
      </Box.Column>
    </Box.Row>
  );
};

type IconProps = {
  /** Icon as child */
  children?: React.ReactNode;
  /** Props for the icon */
  icon?: React.ReactNode;
} & React.HTMLAttributes<HTMLDivElement>;

const Icon = ({ children, icon, ...props }: IconProps) => {
  if (!children && !icon) return null;
  return (
    <div {...props} className={sharedStyles.dropdownMenuItemIndicator}>
      {children || icon}
    </div>
  );
};

export const DropdownMenu = Object.assign(
  {},
  {
    Root: DropdownMenuRoot,
    Trigger: DropdownMenuTrigger,
    Content: DropdownMenuContent,
    Label: DropdownMenuLabel,
    Item: DropdownMenuItem,
    Group: DropdownMenuGroup,
    CheckboxItem: DropdownMenuCheckboxItem,
    RadioGroup: DropdownMenuRadioGroup,
    RadioItem: DropdownMenuRadioItem,
    Separator: DropdownMenuSeparator,
    Arrow: DropdownMenuArrow,
    Portal: DropdownMenuPortal,
    Sub: DropdownMenuSub,
    SubTrigger: DropdownMenuSubTrigger,
    SubContent: DropdownMenuSubContent,
    ItemIndicator: DropdownMenuItemIndicator,
    // Custom
    RightSlot: RightSlot,
    ShortcutCombo,
    Icon,
    Message,
    CustomItem: DropdownMenuCustomItem,
  }
);

DropdownMenu.Trigger.displayName = 'DropdownMenu.Trigger';
DropdownMenu.Content.displayName = 'DropdownMenu.Content';
DropdownMenu.Label.displayName = 'DropdownMenu.Label';
DropdownMenu.Item.displayName = 'DropdownMenu.Item';
DropdownMenu.Group.displayName = 'DropdownMenu.Group';
DropdownMenu.CheckboxItem.displayName = 'DropdownMenu.CheckboxItem';
DropdownMenu.RadioGroup.displayName = 'DropdownMenu.RadioGroup';
DropdownMenu.RadioItem.displayName = 'DropdownMenu.RadioItem';
DropdownMenu.Separator.displayName = 'DropdownMenu.Separator';
DropdownMenu.Arrow.displayName = 'DropdownMenu.Arrow';
DropdownMenu.Portal.displayName = 'DropdownMenu.Portal';
DropdownMenu.Sub.displayName = 'DropdownMenu.Sub';
DropdownMenu.SubTrigger.displayName = 'DropdownMenu.SubTrigger';
DropdownMenu.SubContent.displayName = 'DropdownMenu.SubContent';
DropdownMenu.ItemIndicator.displayName = 'DropdownMenu.ItemIndicator';
DropdownMenu.CustomItem.displayName = 'DropdownMenu.CustomItem';
